import { PDFDocument, rgb, type PDFPage } from "pdf-lib";
import { saveAs } from "file-saver";
import type { BulkAcceptExportPayload } from "./types";
import { bulkAcceptExportFileBaseName } from "./buildBulkAcceptExportPayload";
import {
  renderThaiLeftRightLineToPng,
  renderThaiModalSummaryGridToPng,
  renderThaiTableRowToPng,
  renderThaiTextToPng,
  type ModalSummaryPdfRow,
  type TableColAlign,
  type ThaiTextPngResult,
} from "utils/pdf/renderThaiTextToPng";

const PAGE_WIDTH = 595.28;
const PAGE_HEIGHT = 841.89;
const MARGIN = 40;
const FOOTER_RESERVE = 28;
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2;

const PDF_TITLE = "สรุปรายการสินค้าก่อนยืนยันการรับคำสั่งซื้อ";

const FONT_SIZE_TABLE = 10;
/** หัวข้อเอกสาร — ใหญ่กว่ารายละเอียด/ตารางเล็กน้อย */
const FONT_SIZE_TITLE = 11;
const FONT_SIZE_FOOTER = 9;

const TABLE_HEADERS = ["ลำดับ", "ชื่อสินค้า", "ชื่อ SKU", "จำนวน", "สถานะ"] as const;
/**
 * ลำดับ | ชื่อสินค้า | ชื่อ SKU | จำนวน | สถานะ
 * พื้นที่หลังหัก ลำดับ+จำนวน+สถานะ → แบ่งครึ่งให้ชื่อสินค้า / ชื่อ SKU
 */
const COL_WIDTHS_PT = [28, 190, 190, 43, 104];
/*const COL_WIDTHS_PT = (() => {
  const rankW = 22;
  const qtyW = 40;
  const statusW = 145;
  const nameBlock = CONTENT_WIDTH - rankW - qtyW - statusW;
  const productW = Math.floor(nameBlock / 2);
  const skuW = nameBlock - productW;
  return [rankW, productW, skuW, qtyW, statusW];
})();*/
const TABLE_COL_ALIGN: TableColAlign[] = ["center", "left", "left", "right", "center"];

const COLOR_DANGER = "#f5222d";
const TABLE_LINE_HEIGHT_PT = 13;

type PageContext = {
  doc: PDFDocument;
  page: PDFPage;
  y: number;
};

async function drawPng(
  ctx: PageContext,
  png: ThaiTextPngResult,
  x = MARGIN,
  maxWidthPt = CONTENT_WIDTH
): Promise<number> {
  const img = await ctx.doc.embedPng(png.bytes);
  let w = png.widthPt;
  let h = png.heightPt;
  if (w > maxWidthPt) {
    const ratio = maxWidthPt / w;
    w = maxWidthPt;
    h *= ratio;
  }
  ctx.page.drawImage(img, {
    x,
    y: ctx.y - h,
    width: w,
    height: h,
  });
  return h;
}

function newPage(ctx: PageContext): void {
  ctx.page = ctx.doc.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
  ctx.y = PAGE_HEIGHT - MARGIN;
}

async function drawTableHeader(
  ctx: PageContext,
  options?: { topRule?: boolean }
): Promise<void> {
  if (options?.topRule) {
    ctx.page.drawLine({
      start: { x: MARGIN, y: ctx.y },
      end: { x: PAGE_WIDTH - MARGIN, y: ctx.y },
      thickness: 0.5,
      color: rgb(0.75, 0.75, 0.75),
    });
    ctx.y -= 8;
  }

  const png = await renderThaiTableRowToPng(
    [...TABLE_HEADERS],
    COL_WIDTHS_PT,
    FONT_SIZE_TABLE,
    { color: "#000000", lineHeightPt: TABLE_LINE_HEIGHT_PT, alignments: TABLE_COL_ALIGN }
  );
  const h = await drawPng(ctx, png);
  ctx.y -= h;
  ctx.page.drawLine({
    start: { x: MARGIN, y: ctx.y },
    end: { x: PAGE_WIDTH - MARGIN, y: ctx.y },
    thickness: 0.5,
    color: rgb(0.75, 0.75, 0.75),
  });
  ctx.y -= 6;
}

async function drawSkuRow(
  ctx: PageContext,
  row: BulkAcceptExportPayload["skuRows"][number]
): Promise<void> {
  const isUnavailable = row.statusLabel === "ไม่พร้อมจำหน่าย";
  const rowPng = await renderThaiTableRowToPng(
    [String(row.rank), row.productName, row.skuLabel, String(row.qty), row.statusLabel],
    COL_WIDTHS_PT,
    FONT_SIZE_TABLE,
    {
      lineHeightPt: TABLE_LINE_HEIGHT_PT,
      alignments: TABLE_COL_ALIGN,
      colors: [
        "#000000",
        "#000000",
        "#000000",
        "#000000",
        isUnavailable ? COLOR_DANGER : "#000000",
      ],
    }
  );

  if (ctx.y - rowPng.heightPt < MARGIN + FOOTER_RESERVE) {
    newPage(ctx);
    await drawTableHeader(ctx);
  }

  const h = await drawPng(ctx, rowPng);
  ctx.y -= h + 2;
}

async function drawPageFooters(doc: PDFDocument): Promise<void> {
  const pages = doc.getPages();
  const total = pages.length;
  for (let i = 0; i < total; i += 1) {
    const footerPng = await renderThaiTextToPng(`หน้า ${i + 1} / ${total}`, FONT_SIZE_FOOTER, {
      color: "#666666",
      paddingTopPx: 6,
      paddingBottomPx: 6,
    });
    const img = await doc.embedPng(footerPng.bytes);
    const w = footerPng.widthPt;
    const h = footerPng.heightPt;
    pages[i].drawImage(img, {
      x: (PAGE_WIDTH - w) / 2,
      y: MARGIN - h - 4,
      width: w,
      height: h,
    });
  }
}

export async function createBulkAcceptSummaryPdf(
  payload: BulkAcceptExportPayload
): Promise<void> {
  const doc = await PDFDocument.create();
  const page = doc.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
  const ctx: PageContext = { doc, page, y: PAGE_HEIGHT - MARGIN };

  const titlePng = await renderThaiTextToPng(PDF_TITLE, FONT_SIZE_TITLE, {
    widthPt: CONTENT_WIDTH,
    textAlign: "left",
    paddingTopPx: 8,
    paddingBottomPx: 8,
  });
  ctx.y -= (await drawPng(ctx, titlePng)) + 8;

  const metaPng = await renderThaiLeftRightLineToPng(
    `ชื่อผู้ประกอบการ: ${payload.factoryName}`,
    `วันที่ส่งออก: ${payload.exportedAtLabel}`,
    FONT_SIZE_TABLE,
    CONTENT_WIDTH
  );
  ctx.y -= (await drawPng(ctx, metaPng)) + 10;

  const summaryRows: ModalSummaryPdfRow[] = [
    { label: "คำสั่งซื้อ", value: String(payload.orderCount), unit: "รายการ" },
    { label: "รายการสินค้า (ตัวเลือกสินค้า)", value: String(payload.skuCount), unit: "SKU" },
    { label: "การสั่งซื้อ", value: String(payload.totalPieces), unit: "ชิ้น" },
  ];
  if (payload.notReadyToConfirmCount > 0) {
    summaryRows.push(
      {
        label: "คำสั่งซื้อที่พร้อมยืนยัน",
        value: String(payload.readyToConfirmCount),
        unit: "รายการ",
      },
      {
        label: "คำสั่งซื้อที่ไม่พร้อมยืนยัน",
        value: String(payload.notReadyToConfirmCount),
        unit: "รายการ",
        valueColor: COLOR_DANGER,
      }
    );
  }

  const summaryPng = await renderThaiModalSummaryGridToPng(
    summaryRows,
    CONTENT_WIDTH,
    FONT_SIZE_TABLE
  );
  ctx.y -= (await drawPng(ctx, summaryPng)) + 12;

  await drawTableHeader(ctx, { topRule: true });
  for (const row of payload.skuRows) {
    await drawSkuRow(ctx, row);
  }

  await drawPageFooters(doc);

  const pdfBytes = await doc.save();
  const blob = new Blob([pdfBytes], { type: "application/pdf" });
  saveAs(blob, `${bulkAcceptExportFileBaseName()}.pdf`);
}
